import React, { useEffect, useMemo, useState } from 'react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useNotifications, useMarkNotificationRead, NotificationStub, ApiError } from '@/services/api';
import { useToasts } from '@/state/ToastContext';
import useDebounce from '@/hooks/useDebounce';
import PaginationControls from '@/components/PaginationControls';

const STATUS_TABS = ['all', 'pending', 'scheduled', 'sent', 'delivered', 'read', 'failed'] as const;
const CHANNEL_OPTIONS = ['all', 'care', 'email', 'sms', 'push', 'system'] as const;

const formatChannelLabel = (channel?: string | null) => {
  if (!channel) return 'IN-APP';
  const normalized = channel.toLowerCase();
  if (normalized === 'care') return 'CARE ORDERS';
  if (normalized === 'email') return 'EMAIL';
  if (normalized === 'sms') return 'SMS';
  if (normalized === 'push') return 'PUSH';
  return channel.toUpperCase();
};

const getChannelBadgeClass = (channel?: string | null) => {
  switch ((channel ?? '').toLowerCase()) {
    case 'care':
      return 'bg-emerald-100 text-emerald-700';
    case 'email':
      return 'bg-blue-100 text-blue-700';
    case 'sms':
      return 'bg-violet-100 text-violet-700';
    case 'push':
      return 'bg-indigo-100 text-indigo-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return '剛剛';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('zh-TW', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const NotificationsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_TABS)[number]>('all');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [channelFilter, setChannelFilter] = useState<(typeof CHANNEL_OPTIONS)[number]>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounce(search, 400);
  const normalizedStatus = statusFilter === 'all' ? undefined : statusFilter;
  const filters = useMemo(
    () => ({
      query: debouncedSearch.trim() || undefined,
      status: normalizedStatus,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      channel: channelFilter === 'all' ? undefined : channelFilter,
      page,
      pageSize,
    }),
    [debouncedSearch, normalizedStatus, startDate, endDate, channelFilter, page, pageSize],
  );
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, normalizedStatus, startDate, endDate, channelFilter]);
  const {
    data: notificationsPayload,
    isLoading,
    error,
  } = useNotifications(filters);
  const notifications = useMemo(
    () => notificationsPayload?.value ?? [],
    [notificationsPayload?.value],
  );
  const meta = notificationsPayload?.meta ?? { page, pageSize, total: 0, totalPages: 1, hasMore: false };
  const markNotificationRead = useMarkNotificationRead();
  const { pushToast } = useToasts();
  const [bulkMarkPending, setBulkMarkPending] = useState(false);

  const unreadNotifications = useMemo(
    () => notifications.filter((notification) => (notification.status ?? '').toLowerCase() !== 'read'),
    [notifications],
  );

  const permissionError = error instanceof ApiError && (error.status === 401 || error.status === 403);

  const handleMarkRead = async (notification: NotificationStub) => {
    if ((notification.status ?? '').toLowerCase() === 'read') return;
    try {
      await markNotificationRead.mutateAsync({ notificationId: notification.id });
      pushToast({ tone: 'success', message: '通知已標記為已讀' });
    } catch (err) {
      pushToast({ tone: 'error', message: '無法標記為已讀', detail: err instanceof Error ? err.message : undefined });
    }
  };

  const handleMarkAllRead = async () => {
    if (permissionError) return;
    const snapshot = unreadNotifications.slice();
    if (!snapshot.length) return;
    setBulkMarkPending(true);
    try {
      for (const notification of snapshot) {
        await markNotificationRead.mutateAsync({ notificationId: notification.id });
      }
      pushToast({
        tone: 'success',
        message: snapshot.length === 1 ? '已標記 1 則通知已讀' : `已標記 ${snapshot.length} 則通知已讀`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        message: '批次標記失敗',
        detail: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBulkMarkPending(false);
    }
  };

  const bulkDisabled =
    permissionError || unreadNotifications.length === 0 || markNotificationRead.isPending || bulkMarkPending;
  const totalPages = meta?.totalPages ?? Math.max(1, Math.ceil((meta?.total ?? 0) / (meta?.pageSize ?? pageSize)));
  const startIndex = notifications.length ? (meta.page - 1) * meta.pageSize + 1 : 0;
  const endIndex = notifications.length ? startIndex + notifications.length - 1 : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="section-title">訊息中心</p>
          <h1 className="text-2xl font-semibold text-slate-900">通知列表</h1>
          <p className="text-sm text-slate-500">檢視與管理系統通知、提醒與外部訊息。</p>
        </div>
      </div>

      <section className="glass-panel space-y-4 p-6">
        <div className="flex flex-wrap items-center gap-3">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setStatusFilter(tab)}
              className={`rounded-2xl px-4 py-1 text-xs font-semibold transition ${
                statusFilter === tab ? 'bg-brand-blue text-white' : 'bg-white text-slate-500 shadow-sm'
              }`}
            >
              {tab === 'all' ? '全部' : tab.toUpperCase()}
            </button>
          ))}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <label className="text-xs font-semibold text-slate-500">
              起始
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
              />
            </label>
            <label className="text-xs font-semibold text-slate-500">
              結束
              <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
              />
            </label>
            <label className="text-xs font-semibold text-slate-500">
              Channel
              <select
                value={channelFilter}
                onChange={(event) => setChannelFilter(event.target.value as (typeof CHANNEL_OPTIONS)[number])}
                className="mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
              >
                {CHANNEL_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option === 'all' ? '全部' : option.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={bulkDisabled}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
            >
              {bulkMarkPending
                ? '批次處理中…'
                : unreadNotifications.length > 0
                  ? `標記全部已讀 (${unreadNotifications.length})`
                  : '沒有未讀通知'}
            </button>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="搜尋訊息或收件人"
              className="min-w-[200px] rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
              disabled={permissionError}
            />
          </div>
        </div>

        {error && !permissionError && (
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700" role="alert">
            無法載入通知：{error instanceof Error ? error.message : '未知錯誤'}
          </p>
        )}

        <div className="space-y-3">
          {permissionError ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-slate-400">
              您沒有檢視通知的權限。如需存取通知歷史，請聯絡系統管理員。
            </div>
          ) : isLoading ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-slate-400">
              取得通知中…
            </div>
          ) : notifications.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-slate-400">
              無符合條件的通知。
            </div>
          ) : (
            notifications.map((notification) => {
              const isRead = (notification.status ?? '').toLowerCase() === 'read';
              const metadata = (notification.metadata ?? {}) as Record<string, any>;
              const showPriorityBadge = Boolean(metadata?.slaMinutes || metadata?.priorityBadge);
              return (
                <div
                  key={notification.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-4"
                >
                  <div className="flex flex-1 items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                      <BellAlertIcon className="h-5 w-5 text-slate-500" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900">{notification.message}</p>
                        {showPriorityBadge && (
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                              metadata?.slaBreached ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            Priority
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${getChannelBadgeClass(notification.channel)}`}
                        >
                          {formatChannelLabel(notification.channel)}
                        </span>
                        <span>·</span>
                        <span>{notification.to ?? '未指定收件人'}</span>
                      </div>
                      <p className="text-[11px] text-slate-400">{formatTimestamp(notification.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase ${
                        isRead
                          ? 'bg-emerald-50 text-emerald-600'
                          : (notification.status ?? '').toLowerCase() === 'failed'
                            ? 'bg-rose-50 text-rose-600'
                            : 'bg-amber-50 text-amber-600'
                      }`}
                    >
                      {notification.status ?? 'pending'}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleMarkRead(notification)}
                      disabled={isRead || markNotificationRead.isPending || bulkMarkPending}
                      className="rounded-xl border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 disabled:opacity-50"
                    >
                      {isRead ? '已讀' : '標記已讀'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {meta.total > 0 && (
          <PaginationControls
            page={meta.page}
            perPage={meta.pageSize}
            total={meta.total}
            totalPages={totalPages}
            canNext={meta.hasMore}
            canPrev={meta.page > 1}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={setPage}
            onPerPageChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        )}
      </section>
    </div>
  );
};

export default NotificationsPage;
